import { useState } from "react";
import { Box, Button, Typography, IconButton } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { X, Plus } from "lucide-react";
import { CreateVideoRoomRequest } from "protogen/video_service_pb";
import { VideoRoom } from "protogen/video_pb";
import { useCreateVideoRoom } from "services/video";
import CopyButton from "components/common/CopyButton";

interface VideoCallSetupProps {
  videoRoom?: VideoRoom;
  onVideoRoomChange: (videoRoom: VideoRoom | undefined) => void;
  loading?: boolean;
  familyRef?: string;
}

export default function VideoCallSetup({
  videoRoom,
  onVideoRoomChange,
  loading = false,
  familyRef,
}: VideoCallSetupProps) {
  const theme = useTheme();
  const [creating, setCreating] = useState(false);

  const { request: createVideoRoom } = useCreateVideoRoom((response) => {
    if (response?.videoRoom) {
      onVideoRoomChange(response.videoRoom);
      setCreating(false);
    }
  });

  const generateVideoRoom = async () => {
    setCreating(true);
    const request = new CreateVideoRoomRequest({
      recordingEnabled: false,
      familyRef: familyRef || "",
    });
    await createVideoRoom(request);
    setCreating(false);
  };

  const removeVideoCall = () => {
    onVideoRoomChange(undefined);
  };

  if (!videoRoom) {
    return (
      <Button
        variant="text"
        startIcon={<Plus />}
        onClick={generateVideoRoom}
        disabled={loading || creating}
      >
        {creating ? "Creating video room..." : "Add video call link"}
      </Button>
    );
  }

  return (
    <Box
      sx={{
        display: "flex",
        padding: "12px 16px",
        borderRadius: "12px",
        backgroundColor: "#FAF9FA",
        justifyContent: "space-between",
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Typography
          variant="bodyHeavy"
          sx={{ display: "flex", alignItems: "center", gap: "8px" }}
        >
          Video meeting
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Attendees will be sent a link to join.
        </Typography>
      </Box>
      <Box sx={{ display: "flex", alignItems: "center", gap: "12px" }}>
        <CopyButton
          value={
            videoRoom.uuid
              ? `${window.location.origin}/video/${videoRoom.uuid}`
              : ""
          }
          variant="text"
          label="Copy meeting link"
          disabled={loading || !videoRoom.uuid}
        />
        <IconButton onClick={removeVideoCall} size="small" disabled={loading}>
          <X size={24} color={theme.palette.text.tertiary} />
        </IconButton>
      </Box>
    </Box>
  );
}
